/**
 * Lumyn Live — trainer host dashboard (Phase 2 redesign).
 *
 * Page shell only: owns layout + the session-list toolbar. All data/real-time
 * wiring lives in useHostSession; the active-session surface is HostControl;
 * list rows are ActiveSessionCard. (Decomposed in the Phase 2 refactor.)
 */
import { Flame, Search, ArrowUpDown } from 'lucide-react';
import { EmptyState } from '../components/common/EmptyState';
import { SkeletonCard } from '../components/common/SkeletonCard';
import NewSessionCreator from '../components/live/NewSessionCreator';
import { HostControl } from '../components/live/host/HostControl';
import { ActiveSessionCard } from '../components/live/host/ActiveSessionCard';
import { useHostSession } from '../hooks/useHostSession';

export default function LiveHostPage() {
  const {
    sessions,
    loading,
    active,
    setActive,
    wsConnected,
    query,
    setQuery,
    sortMode,
    setSortMode,
    visibleSessions,
    load,
    handleSessionRenamed,
    selectSession,
  } = useHostSession();

  if (active) {
    return (
      <HostControl
        session={active}
        wsConnected={wsConnected}
        onUpdated={(s) => setActive(s)}
        onClose={() => { setActive(null); load(); }}
      />
    );
  }

  return (
    <div className="space-y-5 relative" data-testid="live-host-page">
      <div aria-hidden className="page-blob bg-[var(--lumyn-live)]" />
      <div className="relative z-10 space-y-5">
        <div className="space-y-1.5">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--lumyn-live)]/10 text-[var(--lumyn-live)] text-[11px] font-semibold uppercase tracking-[0.14em]">
            <Flame className="w-3.5 h-3.5" />Live sessions
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{'Lumyn Live'}</h1>
          <p className="text-sm text-muted-foreground max-w-prose">
            Run a live floor-briefing quiz. Participants join from their phones in seconds — no logins needed.
          </p>
        </div>
        <NewSessionCreator onCreated={(s) => setActive(s)} />
        {loading ? (
          <SkeletonCard />
        ) : sessions.length === 0 ? (
          <EmptyState
            icon={Flame}
            title={'No Live sessions yet'}
            description={'Create a session, share the 6-digit code, and run a real-time quiz.'}
          />
        ) : (
          <>
            {/* Search + sort toolbar — only mounted when there's actually
                something to filter, to keep the empty state clean. */}
            <div className="flex flex-col sm:flex-row gap-2" data-testid="sessions-toolbar">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by session name or ref code"
                  className="w-full text-sm pl-8 pr-3 py-2 rounded-md border border-border bg-background outline-none focus:ring-2 focus:ring-[var(--lumyn-live)]/40"
                  data-testid="sessions-search-input"
                />
              </div>
              <div className="relative inline-flex items-center">
                <ArrowUpDown className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                <select
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value)}
                  className="text-sm pl-8 pr-3 py-2 rounded-md border border-border bg-background outline-none focus:ring-2 focus:ring-[var(--lumyn-live)]/40 appearance-none cursor-pointer"
                  data-testid="sessions-sort-select"
                >
                  <option value="latest">Latest activity</option>
                  <option value="oldest">Oldest first</option>
                  <option value="name">Name (A→Z)</option>
                </select>
              </div>
            </div>
            {visibleSessions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6" data-testid="sessions-search-empty">
                No sessions match &quot;{query}&quot;.
              </p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {visibleSessions.map((s) => (
                  <ActiveSessionCard
                    key={s.session_id}
                    session={s}
                    onRenamed={handleSessionRenamed}
                    onSelect={selectSession}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
